/**
 * CoLab News Slider Module
 * Кастомний swiper для новин з infinite loop без ривків
 *
 * Логіка loop:
 * - Клонуємо слайди на початок і кінець
 * - Телепортуємо ЗАВЧАСНО (за 1 слайд до краю) під час transition
 * - Це робить перехід непомітним для користувача
 */

export const News = {
    // DOM Elements
    container: null,
    track: null,
    slides: null,
    originalSlides: [],
    prevBtn: null,
    nextBtn: null,
    pagination: null,

    // State
    currentIndex: 0,
    realIndex: 0,
    slidesPerView: 1,
    slideWidth: 480,
    gap: 24,
    totalSlides: 0,
    clonesAtStart: 0,
    clonesAtEnd: 0,
    isTransitioning: false,

    // Touch/drag state
    startX: 0,
    currentX: 0,
    isDragging: false,

    // Autoplay
    autoplayTimer: null,

    // ============================================
    // Default Configuration
    // ============================================
    defaults: {
        slideWidth: 480,
        slidesPerView: 'auto',
        gap: 24,
        speed: 500,
        loop: false,
        autoplay: 0,
        pauseOnHover: true,
        useJsGap: true,

        breakpoints: {
            0: {
                slidesPerView: 1,
                slideWidth: 'auto'
            },
            768: {
                slidesPerView: 1.5,
                slideWidth: 'auto'
            },
            992: {
                slidesPerView: 'auto',
                slideWidth: 480
            },
            1200: {
                slidesPerView: 'auto',
                slideWidth: 480
            }
        }
    },

    config: {},

    // ============================================
    // Initialization
    // ============================================
    init: function(options) {
        this.config = this.mergeDeep({}, this.defaults, options || {});

        this.container = document.querySelector('.news__slider');
        this.track = document.querySelector('.news__track');
        this.slides = document.querySelectorAll('.news__slide');
        this.prevBtn = document.querySelector('.news__nav-btn--prev');
        this.nextBtn = document.querySelector('.news__nav-btn--next');
        this.pagination = document.querySelector('.news__pagination');

        if (!this.track || !this.slides.length) {
            console.log('[CoLab.News] No slider found');
            return;
        }

        // Зберігаємо оригінальні слайди
        this.originalSlides = Array.from(this.slides);
        this.totalSlides = this.originalSlides.length;
        this.gap = this.config.gap;

        // Прибираємо CSS gap якщо контролюємо через JS
        if (this.config.useJsGap) {
            this.track.style.gap = '0px';
        }

        // Створюємо клони для loop
        if (this.config.loop && this.totalSlides > 1) {
            this.createClones();
        }

        this.bindEvents();
        this.calculateDimensions();

        // Autoplay
        if (this.config.autoplay > 0) {
            this.startAutoplay();
        }

        console.log('[CoLab.News] Initialized');
    },

    // ============================================
    // Create clones for infinite loop
    // ============================================
    createClones: function() {
        // Клонуємо достатньо слайдів для плавного переходу
        var cloneCount = Math.min(this.totalSlides, 3);

        // Клонуємо останні слайди НА ПОЧАТОК
        for (var i = this.totalSlides - 1; i >= this.totalSlides - cloneCount; i--) {
            var clone = this.originalSlides[i].cloneNode(true);
            clone.classList.add('is-clone');
            clone.setAttribute('data-clone-of', i);
            this.track.insertBefore(clone, this.track.firstChild);
        }
        this.clonesAtStart = cloneCount;

        // Клонуємо перші слайди В КІНЕЦЬ
        for (var j = 0; j < cloneCount; j++) {
            var cloneEnd = this.originalSlides[j].cloneNode(true);
            cloneEnd.classList.add('is-clone');
            cloneEnd.setAttribute('data-clone-of', j);
            this.track.appendChild(cloneEnd);
        }
        this.clonesAtEnd = cloneCount;

        // Оновлюємо список слайдів
        this.slides = this.track.querySelectorAll('.news__slide');

        // Початковий індекс - перший оригінальний слайд
        this.currentIndex = this.clonesAtStart;

        console.log('[CoLab.News] Clones created:', {
            atStart: this.clonesAtStart,
            atEnd: this.clonesAtEnd,
            total: this.slides.length,
            startIndex: this.currentIndex
        });
    },

    // ============================================
    // Deep merge helper
    // ============================================
    mergeDeep: function(target) {
        var sources = Array.prototype.slice.call(arguments, 1);
        var self = this;

        sources.forEach(function(source) {
            if (!source) return;

            Object.keys(source).forEach(function(key) {
                if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                    if (!target[key]) target[key] = {};
                    self.mergeDeep(target[key], source[key]);
                } else {
                    target[key] = source[key];
                }
            });
        });

        return target;
    },

    // ============================================
    // Get current breakpoint settings
    // ============================================
    getCurrentBreakpoint: function() {
        var width = window.innerWidth;
        var breakpoints = this.config.breakpoints;
        var currentSettings = {
            slidesPerView: this.config.slidesPerView,
            slideWidth: this.config.slideWidth
        };

        var breakpointKeys = Object.keys(breakpoints)
            .map(Number)
            .sort(function(a, b) { return a - b; });

        for (var i = 0; i < breakpointKeys.length; i++) {
            var bp = breakpointKeys[i];
            if (width >= bp) {
                currentSettings = {
                    slidesPerView: breakpoints[bp].slidesPerView,
                    slideWidth: breakpoints[bp].slideWidth
                };
            }
        }

        return currentSettings;
    },

    // ============================================
    // Calculate dimensions
    // ============================================
    calculateDimensions: function() {
        var settings = this.getCurrentBreakpoint();
        var containerWidth = this.container.offsetWidth;

        if (settings.slideWidth === 'auto') {
            if (typeof settings.slidesPerView === 'number') {
                var gapsCount = Math.ceil(settings.slidesPerView);
                var totalGapWidth = gapsCount * this.gap;
                this.slideWidth = (containerWidth - totalGapWidth) / settings.slidesPerView;
                this.slidesPerView = settings.slidesPerView;
            } else {
                this.slideWidth = containerWidth;
                this.slidesPerView = 1;
            }
        } else {
            this.slideWidth = settings.slideWidth;

            if (settings.slidesPerView === 'auto') {
                this.slidesPerView = (containerWidth + this.gap) / (this.slideWidth + this.gap);
            } else {
                this.slidesPerView = settings.slidesPerView;
            }
        }

        // Застосовуємо стилі до слайдів
        var self = this;
        this.slides.forEach(function(slide) {
            slide.style.width = self.slideWidth + 'px';
            slide.style.flexShrink = '0';

            if (self.config.useJsGap) {
                slide.style.marginRight = self.gap + 'px';
            }
        });

        this.updatePosition(false);
        this.updateNavigation();
        this.updatePagination();
    },

    // ============================================
    // Event Bindings
    // ============================================
    bindEvents: function() {
        var self = this;

        // Navigation buttons
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', function() {
                self.prev();
            });
        }
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', function() {
                self.next();
            });
        }

        // Touch events
        this.container.addEventListener('touchstart', function(e) {
            self.handleDragStart(e);
        }, { passive: true });

        this.container.addEventListener('touchmove', function(e) {
            self.handleDragMove(e);
        }, { passive: true });

        this.container.addEventListener('touchend', function() {
            self.handleDragEnd();
        });

        // Mouse events
        this.container.addEventListener('mousedown', function(e) {
            e.preventDefault();
            self.handleDragStart(e);
        });
        this.container.addEventListener('mousemove', function(e) {
            self.handleDragMove(e);
        });
        this.container.addEventListener('mouseup', function() {
            self.handleDragEnd();
        });
        this.container.addEventListener('mouseleave', function() {
            self.handleDragEnd();
        });

        // Pause autoplay on hover
        if (this.config.pauseOnHover && this.config.autoplay > 0) {
            this.container.addEventListener('mouseenter', function() {
                self.stopAutoplay();
            });
            this.container.addEventListener('mouseleave', function() {
                if (!self.isDragging) {
                    self.startAutoplay();
                }
            });
        }

        // Keyboard
        this.container.setAttribute('tabindex', '0');
        this.container.addEventListener('keydown', function(e) {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                self.prev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                self.next();
            }
        });

        // Resize
        var resizeTimeout;
        window.addEventListener('resize', function() {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(function() {
                self.calculateDimensions();
            }, 150);
        });
    },

    // ============================================
    // Get offset for index
    // ============================================
    getOffset: function(index) {
        return index * (this.slideWidth + this.gap);
    },

    // ============================================
    // Position Updates
    // ============================================
    updatePosition: function(animate) {
        var self = this;
        animate = animate !== false;

        var offset = this.getOffset(this.currentIndex);
        var speed = this.config.speed || 500;

        if (animate) {
            this.isTransitioning = true;
            this.track.style.transition = 'transform ' + speed + 'ms cubic-bezier(0.25, 0.1, 0.25, 1)';

            // Після завершення анімації перевіряємо чи потрібна телепортація
            setTimeout(function() {
                self.isTransitioning = false;
                self.checkAndTeleport();
            }, speed);
        } else {
            this.track.style.transition = 'none';
        }

        this.track.style.transform = 'translateX(-' + offset + 'px)';
        this.updateSlideStates();
        this.updateRealIndex();
    },

    // ============================================
    // Check and teleport for seamless loop
    // ============================================
    checkAndTeleport: function() {
        if (!this.config.loop || this.isTransitioning) return;

        var needsTeleport = false;
        var newIndex = this.currentIndex;

        // Якщо дійшли до клонів в кінці - телепортуємо на початок
        if (this.currentIndex >= this.clonesAtStart + this.totalSlides) {
            newIndex = this.clonesAtStart + (this.currentIndex - this.clonesAtStart - this.totalSlides);
            needsTeleport = true;
        }
        // Якщо дійшли до клонів на початку - телепортуємо в кінець
        else if (this.currentIndex < this.clonesAtStart) {
            newIndex = this.clonesAtStart + this.totalSlides - (this.clonesAtStart - this.currentIndex);
            needsTeleport = true;
        }

        if (needsTeleport) {
            this.currentIndex = newIndex;
            this.updatePosition(false); // Телепортуємо без анімації
        }
    },

    updateSlideStates: function() {
        var self = this;
        var visibleCount = Math.ceil(this.slidesPerView);

        this.slides.forEach(function(slide, index) {
            slide.classList.remove('is-active', 'is-prev', 'is-next', 'is-visible');

            if (index === self.currentIndex) {
                slide.classList.add('is-active');
            } else if (index === self.currentIndex - 1) {
                slide.classList.add('is-prev');
            } else if (index === self.currentIndex + 1) {
                slide.classList.add('is-next');
            }

            if (index >= self.currentIndex && index < self.currentIndex + visibleCount + 1) {
                slide.classList.add('is-visible');
            }
        });
    },

    updateRealIndex: function() {
        if (!this.config.loop) {
            this.realIndex = this.currentIndex;
            return;
        }

        // Конвертуємо currentIndex в realIndex (індекс оригінального слайду)
        this.realIndex = this.currentIndex - this.clonesAtStart;

        // Нормалізуємо
        while (this.realIndex < 0) {
            this.realIndex += this.totalSlides;
        }
        while (this.realIndex >= this.totalSlides) {
            this.realIndex -= this.totalSlides;
        }
    },

    updateNavigation: function() {
        // В loop режимі кнопки завжди активні
        if (this.config.loop) {
            if (this.prevBtn) {
                this.prevBtn.disabled = false;
                this.prevBtn.classList.remove('is-disabled');
            }
            if (this.nextBtn) {
                this.nextBtn.disabled = false;
                this.nextBtn.classList.remove('is-disabled');
            }
            return;
        }

        var maxIndex = Math.max(0, this.slides.length - Math.ceil(this.slidesPerView));

        if (this.prevBtn) {
            this.prevBtn.disabled = this.currentIndex === 0;
            this.prevBtn.classList.toggle('is-disabled', this.currentIndex === 0);
        }
        if (this.nextBtn) {
            this.nextBtn.disabled = this.currentIndex >= maxIndex;
            this.nextBtn.classList.toggle('is-disabled', this.currentIndex >= maxIndex);
        }
    },

    updatePagination: function() {
        if (!this.pagination) return;

        var self = this;
        this.pagination.innerHTML = '';

        if (this.totalSlides <= 1) return;

        for (var i = 0; i < this.totalSlides; i++) {
            (function(index) {
                var dot = document.createElement('button');
                var isActive = index === self.realIndex;

                dot.className = 'news__pagination-dot' + (isActive ? ' is-active' : '');
                dot.setAttribute('aria-label', 'Слайд ' + (index + 1));
                dot.addEventListener('click', function() {
                    self.goToSlide(index);
                });
                self.pagination.appendChild(dot);
            })(i);
        }
    },

    // ============================================
    // Navigation Methods
    // ============================================
    goToSlide: function(index) {
        if (this.isTransitioning) return;

        if (this.config.loop) {
            this.currentIndex = index + this.clonesAtStart;
        } else {
            var maxIndex = Math.max(0, this.slides.length - Math.ceil(this.slidesPerView));
            this.currentIndex = Math.max(0, Math.min(index, maxIndex));
        }

        this.updatePosition();
        this.updateNavigation();
        this.updatePagination();
    },

    next: function() {
        if (this.isTransitioning) return;

        this.currentIndex++;

        // Для non-loop обмежуємо
        if (!this.config.loop) {
            var maxIndex = Math.max(0, this.slides.length - Math.ceil(this.slidesPerView));
            if (this.currentIndex > maxIndex) {
                this.currentIndex = maxIndex;
                return;
            }
        }

        this.updatePosition();
        this.updateNavigation();
        this.updatePagination();
    },

    prev: function() {
        if (this.isTransitioning) return;

        this.currentIndex--;

        // Для non-loop обмежуємо
        if (!this.config.loop && this.currentIndex < 0) {
            this.currentIndex = 0;
            return;
        }

        this.updatePosition();
        this.updateNavigation();
        this.updatePagination();
    },

    // ============================================
    // Autoplay
    // ============================================
    startAutoplay: function() {
        var self = this;
        this.stopAutoplay();

        if (this.config.autoplay > 0) {
            this.autoplayTimer = setInterval(function() {
                self.next();
            }, this.config.autoplay);
        }
    },

    stopAutoplay: function() {
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    },

    // ============================================
    // Touch/Mouse Drag
    // ============================================
    handleDragStart: function(e) {
        if (this.isTransitioning) return;

        this.isDragging = true;
        this.startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
        this.currentX = this.startX;
        this.track.style.transition = 'none';
        this.container.classList.add('is-dragging');

        this.stopAutoplay();
    },

    handleDragMove: function(e) {
        if (!this.isDragging) return;

        this.currentX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
        var diff = this.currentX - this.startX;
        var currentOffset = this.getOffset(this.currentIndex);

        // Resistance на краях (тільки для non-loop)
        if (!this.config.loop) {
            var maxIndex = Math.max(0, this.slides.length - Math.ceil(this.slidesPerView));
            if ((this.currentIndex === 0 && diff > 0) ||
                (this.currentIndex >= maxIndex && diff < 0)) {
                diff = diff * 0.3;
            }
        }

        this.track.style.transform = 'translateX(' + (-currentOffset + diff) + 'px)';
    },

    handleDragEnd: function() {
        if (!this.isDragging) return;

        this.isDragging = false;
        this.container.classList.remove('is-dragging');

        var diff = this.currentX - this.startX;
        var threshold = this.slideWidth / 4;

        if (diff > threshold) {
            this.prev();
        } else if (diff < -threshold) {
            this.next();
        } else {
            this.updatePosition();
        }

        if (this.config.autoplay > 0) {
            this.startAutoplay();
        }
    },

    // ============================================
    // Public API
    // ============================================
    destroy: function() {
        this.stopAutoplay();

        var clones = this.track.querySelectorAll('.is-clone');
        clones.forEach(function(clone) {
            clone.remove();
        });

        this.track.style.transform = '';
        this.track.style.transition = '';
        this.track.style.gap = '';

        this.slides = this.track.querySelectorAll('.news__slide');
        this.slides.forEach(function(slide) {
            slide.style.width = '';
            slide.style.marginRight = '';
            slide.classList.remove('is-active', 'is-prev', 'is-next', 'is-visible');
        });

        if (this.pagination) {
            this.pagination.innerHTML = '';
        }
    }
};
